import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFamilyData } from "../hooks/useFamilyData";
import { addStarterChores } from "../utils/starterChores";

const quickTourSteps = [
  {
    id: "welcome",
    title: "Welcome! Let's set this up fast",
    body: "We will walk through the simple flow: add kids, create chores, approve, and let kids mark them done.",
    target: "[data-tour='nav-brand']"
  },
  {
    id: "go-settings",
    title: "Step 1: Add your kids",
    body: "Open Settings to add the kids who will do chores.",
    target: "[data-tour='nav-settings']",
    actionLabel: "Go to Settings",
    action: { type: "view", value: "settings" },
    advanceOnView: "settings"
  },
  {
    id: "settings-section",
    title: "You're in Settings",
    body: "Use the tabs across the top to reach Kids.",
    view: "settings",
    target: "[data-tour='settings-section-settings']",
    prepare: { settingsSection: "settings" }
  },
  {
    id: "kids-tab",
    title: "Open the Kids tab",
    body: "Tap Kids to add each child.",
    view: "settings",
    target: "[data-tour='settings-kids-tab']",
    actionLabel: "Show Kids tab",
    action: { type: "settingsTab", value: "kids" },
    prepare: { settingsSection: "settings" }
  },
  {
    id: "add-kids",
    title: "Add a child",
    body: "Add each kid here. You can do this anytime later too.",
    view: "settings",
    target: "[data-tour='settings-add-kid']",
    prepare: { settingsSection: "settings", settingsTab: "kids" }
  },
  {
    id: "go-parent",
    title: "Step 2: Create chores",
    body: "Next, create a few chores in the Parent Hub.",
    target: "[data-tour='nav-parent']",
    actionLabel: "Go to Parent Hub",
    action: { type: "view", value: "parent" },
    advanceOnView: "parent"
  },
  {
    id: "task-card",
    title: "Create your first chore",
    body: "Type a chore name, pick points, and assign it. Start with 2-5 chores.",
    view: "parent",
    target: "[data-tour='task-title-input']"
  },
  {
    id: "starter-pack",
    title: "Need a head start?",
    body: "Add a starter pack of simple chores in one tap.",
    view: "parent",
    target: "[data-tour='parent-task-card']",
    actionLabel: "Add Starter Chores",
    action: { type: "starterChores" }
  },
  {
    id: "approvals",
    title: "Approvals are your daily flow",
    body: "Approve work to award points, or request clarification if needed.",
    view: "parent",
    target: "[data-tour='parent-approvals-card']"
  },
  {
    id: "clarifications",
    title: "Clarifications live here",
    body: "If a kid asks a question, the full thread appears in Clarifications.",
    view: "parent",
    target: "[data-tour='parent-clarifications-card']"
  },
  {
    id: "go-kids",
    title: "Step 3: Kids board",
    body: "Now check the Kids Board where kids mark chores done.",
    target: "[data-tour='nav-board']",
    actionLabel: "Go to Kids Board",
    action: { type: "view", value: "kids" },
    advanceOnView: "kids"
  },
  {
    id: "kids-board",
    title: "Kids mark chores done",
    body: "Kids tap Done here. Approvals happen in the Parent Hub.",
    view: "kids",
    target: "[data-tour='kids-board']"
  },
  {
    id: "home-setup",
    title: "Home setup in 60 seconds",
    body: "See the recommended home setup for shared tablets + kid devices.",
    target: "[data-tour='settings-home-setup']",
    actionLabel: "Open Settings",
    action: { type: "view", value: "settings" },
    advanceOnView: "settings",
    prepare: { settingsSection: "settings", settingsTab: "homeSetup" }
  },
  {
    id: "done",
    title: "You're ready",
    body: "Keep it simple today. Add rewards, schedules, and templates later from Settings when you want more power.",
    target: "[data-tour='nav-brand']"
  }
];

const advancedTourSteps = [
  {
    id: "welcome-advanced",
    title: "Advanced Tour",
    body: "This is a deeper walkthrough of layouts, approvals, clarifications, and feedback.",
    target: "[data-tour='nav-brand']"
  },
  {
    id: "layout-toolbar",
    title: "Edit Layout",
    body: "Use Edit Layout to drag and resize cards. Snap‑to‑grid keeps things aligned.",
    view: "parent",
    target: "[data-tour='layout-toolbar']",
    actionLabel: "Go to Parent Hub",
    action: { type: "view", value: "parent" },
    advanceOnView: "parent"
  },
  {
    id: "layout-controls",
    title: "Drag + Resize",
    body: "Drag cards by the handle and use the corner handle to resize.",
    view: "parent",
    target: "[data-tour='card-controls']"
  },
  {
    id: "repeats-deep",
    title: "Repeat chores",
    body: "Repeating chores live in their own card so you can manage routines fast.",
    view: "parent",
    target: "[data-tour='parent-repeats-card']"
  },
  {
    id: "approvals-deep",
    title: "Approvals",
    body: "Approve, fail, or ask for clarification here.",
    view: "parent",
    target: "[data-tour='parent-approvals-card']"
  },
  {
    id: "clarifications-deep",
    title: "Clarifications",
    body: "Conversation threads live here until the chore is approved or removed.",
    view: "parent",
    target: "[data-tour='parent-clarifications-card']"
  },
  {
    id: "feedback-center",
    title: "Feedback Center",
    body: "Send feedback and read replies from the team.",
    target: "[data-tour='nav-feedback']",
    actionLabel: "Open Feedback",
    action: { type: "view", value: "feedback" },
    advanceOnView: "feedback"
  },
  {
    id: "feedback-page",
    title: "Replies live here",
    body: "Responses to your feedback show up in this list.",
    view: "feedback",
    target: "[data-tour='feedback-page']"
  },
  {
    id: "settings-advanced",
    title: "Settings deep dive",
    body: "Themes, layout toggles, home setup, and pro features are here.",
    target: "[data-tour='nav-settings']",
    actionLabel: "Go to Settings",
    action: { type: "view", value: "settings" },
    advanceOnView: "settings"
  },
  {
    id: "settings-home-setup",
    title: "Home setup guide",
    body: "Use this checklist to set up a shared tablet and kid devices.",
    view: "settings",
    target: "[data-tour='settings-home-setup']",
    prepare: { settingsSection: "settings", settingsTab: "homeSetup" }
  },
  {
    id: "settings-appearance",
    title: "Compact + big tap",
    body: "Compact mode tightens spacing. Big Tap is great for touch screens.",
    view: "settings",
    target: "[data-tour='settings-appearance-tab']",
    prepare: { settingsSection: "settings", settingsTab: "appearance" }
  },
  {
    id: "done-advanced",
    title: "You’re set",
    body: "Use Quick Tour anytime for a refresher. Advanced Tour is always available from Learn.",
    target: "[data-tour='nav-brand']"
  }
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export default function GuidedTour({
  isOpen,
  currentView,
  setView,
  familyId,
  onComplete,
  onSkip,
  mode = "quick"
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const lastViewRef = useRef(currentView);
  const tooltipRef = useRef(null);
  const [actionNotice, setActionNotice] = useState("");
  const { tasks = [] } = useFamilyData(familyId);

  const steps = useMemo(() => (mode === "advanced" ? advancedTourSteps : quickTourSteps), [mode]);
  const step = steps[stepIndex];
  const totalSteps = steps.length;

  useEffect(() => {
    if (!isOpen) return;
    setStepIndex(0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !step) return;
    if (step.prepare && typeof window !== "undefined") {
      if (step.prepare.settingsSection) {
        window.dispatchEvent(
          new CustomEvent("tour:setSettingsSection", { detail: step.prepare.settingsSection })
        );
      }
      if (step.prepare.settingsTab) {
        window.dispatchEvent(
          new CustomEvent("tour:setSettingsTab", { detail: step.prepare.settingsTab })
        );
      }
    }
  }, [isOpen, step]);

  useEffect(() => {
    if (!isOpen || !step?.advanceOnView) return;
    if (currentView === step.advanceOnView && lastViewRef.current !== currentView) {
      setStepIndex((prev) => Math.min(prev + 1, totalSteps - 1));
    }
    lastViewRef.current = currentView;
  }, [isOpen, step, currentView, totalSteps]);

  useEffect(() => {
    if (!isOpen || !step) return;

    let raf;
    const updatePosition = () => {
      const element = step.target ? document.querySelector(step.target) : null;
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        const tooltipWidth = tooltipRef.current?.offsetWidth || 320;
        const tooltipHeight = tooltipRef.current?.offsetHeight || 200;
        const padding = 16;
        const left = clamp(rect.left, padding, window.innerWidth - tooltipWidth - padding);
        let top = rect.bottom + 12;
        if (top + tooltipHeight > window.innerHeight - padding) {
          top = rect.top - tooltipHeight - 12;
        }
        top = clamp(top, padding, window.innerHeight - tooltipHeight - padding);
        setTooltipStyle({
          top,
          left,
          position: "fixed"
        });
      } else {
        setTargetRect(null);
        const tooltipWidth = tooltipRef.current?.offsetWidth || 320;
        const tooltipHeight = tooltipRef.current?.offsetHeight || 200;
        setTooltipStyle({
          top: `calc(50% - ${tooltipHeight / 2}px)`,
          left: `calc(50% - ${tooltipWidth / 2}px)`,
          position: "fixed"
        });
      }
    };

    const handleUpdate = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updatePosition);
    };

    updatePosition();
    window.addEventListener("resize", handleUpdate);
    window.addEventListener("scroll", handleUpdate, true);

    const interval = setInterval(handleUpdate, 300);

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(interval);
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", handleUpdate, true);
    };
  }, [isOpen, step, currentView]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onSkip?.();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onSkip]);

  if (!isOpen || !step) return null;

  const highlightStyle = targetRect
    ? {
        position: "fixed",
        top: targetRect.top - 6,
        left: targetRect.left - 6,
        width: targetRect.width + 12,
        height: targetRect.height + 12
      }
    : null;

  const handleAction = () => {
    if (!step.action) return;
    if (step.action.type === "view" && typeof setView === "function") {
      setView(step.action.value);
    }
    if (step.action.type === "settingsSection") {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("tour:setSettingsSection", { detail: step.action.value })
        );
      }
    }
    if (step.action.type === "settingsTab") {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("tour:setSettingsTab", { detail: step.action.value })
        );
      }
    }
    if (step.action.type === "starterChores") {
      if (!familyId) return;
      addStarterChores({ familyId, tasks }).then((count) => {
        if (count > 0) setActionNotice(`Added ${count} starter chores.`);
        else setActionNotice("Starter chores already added.");
        setTimeout(() => setActionNotice(""), 2500);
      });
    }
  };

  const goNext = () => {
    if (stepIndex === totalSteps - 1) {
      onComplete?.();
      return;
    }
    setStepIndex((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const goBack = () => {
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const isViewMismatch = step.view && step.view !== currentView;

  return (
    <div className="tour-overlay">
      {highlightStyle && <div className="tour-highlight" style={highlightStyle} />}
      <div ref={tooltipRef} className="glass tour-tooltip" style={tooltipStyle}>
        <div className="tour-header">
          <span className="tour-step">Step {stepIndex + 1} of {totalSteps}</span>
          <button className="btn-nav" onClick={() => onSkip?.()} style={{ border: "none" }}>
            ✕
          </button>
        </div>
        <h3 className="tour-title">{step.title}</h3>
        <p className="tour-text">{step.body}</p>
        {actionNotice && <p className="tour-text">{actionNotice}</p>}
        {isViewMismatch && (
          <p className="tour-text" style={{ opacity: 0.7 }}>
            This step lives in the {step.view} view.
          </p>
        )}
        {step.actionLabel && (
          <button className="btn-nav" onClick={handleAction}>
            {step.actionLabel}
          </button>
        )}
        <div className="tour-actions">
          <button className="btn-nav" onClick={() => onSkip?.()}>
            Skip
          </button>
          <div className="tour-actions-right">
            <button className="btn-nav" onClick={goBack} disabled={stepIndex === 0}>
              Back
            </button>
            <button className="btn-nav success-btn" onClick={goNext}>
              {stepIndex === totalSteps - 1 ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
